import { Frame, FrameHeader, RecordType } from "../../..";
import { Sink } from "../../../helpers/serializerHelpers";

export type FrameParseResult = {
    frames: Frame[];
    remainder: Uint8Array;
    logs: string[];
};

/**
 * Pure frame parser. Consumes as many complete frames as possible from `buffer`
 * and returns parsed frames, the unconsumed tail, and a list of log lines the
 * caller can surface to the user. No side effects.
 */
export function parseFramesFromBuffer(buffer: Uint8Array, frameHeaderLength: number): FrameParseResult {
    const parsedFrames: Frame[] = [];
    const logs: string[] = [];
    let offset = 0;

    while (buffer.length - offset >= frameHeaderLength) {
        const headerSink = Sink(buffer.buffer, offset, true);
        const header = new FrameHeader().deserialize(headerSink);
        const packetLength = header.packetLength;
        logs.push(`Frame #${header.frameNumber} v${header.version}.${header.platform}: ${packetLength} bytes, ${header.numTLVs} TLVs`);

        if (!packetLength || packetLength < frameHeaderLength) {
            logs.push(`[${new Date().toLocaleTimeString()}] Parse warning: invalid packet length ${packetLength ?? 0}, sync=0x${header.sync.toString(16)}`);
            break;
        }

        if (buffer.length - offset < packetLength) {
            break;
        }

        const frameSink = Sink(buffer.buffer, offset, true);
        try {
            const frame = new Frame().deserialize(frameSink);
            if (frame) {
                const tlvSummary = frame.tlvs.map(t => `${RecordType[t.getDataType()] ?? t.getDataType()}(${t.getDataLength()}b)`).join(", ");
                logs.push(`  TLVs: [${tlvSummary}]`);
                parsedFrames.push(frame);
            }
        } catch (e) {
            logs.push(`[${new Date().toLocaleTimeString()}] Parse error at offset ${offset}: ${String(e)}`);
            console.error("Frame parse error", e);
        }
        offset += packetLength;
    }

    return {
        frames: parsedFrames,
        remainder: buffer.slice(offset),
        logs,
    };
}

/**
 * Concatenate new bytes onto the existing buffer and return parse results.
 * Kept separate from React state so it can be unit-tested against byte fixtures.
 */
export function appendAndParse(existing: Uint8Array, bytes: Uint8Array, frameHeaderLength: number): FrameParseResult {
    if (!bytes.length) {
        return { frames: [], remainder: existing, logs: [] };
    }
    const combined = new Uint8Array(existing.length + bytes.length);
    combined.set(existing, 0);
    combined.set(bytes, existing.length);
    return parseFramesFromBuffer(combined, frameHeaderLength);
}
